/**
 * Web component: Doogma engraving preview (doogma-engraving-preview)
 * -----------------------------------------------------------------------------
 * Drawer for previewing engraving.
 *
 */

/**
 * Create new <doogma-engraving-preview> custom element.
 */
class DoogmaEngravingPreview extends HTMLElement {

  /**
   * Custom element constructor.
   * - Always call super first in constructor.
   */
  constructor() {
    super()

    this.atcButton = this.querySelector('[js-doogma-engraving-preview="atc"]')
    this.closeButtons = [...this.querySelectorAll('[js-doogma-engraving-preview="close"]')]
    this.editButtons = [...this.querySelectorAll('[js-doogma-engraving-preview="edit"]')]
    this.overlay = this.querySelector('[js-doogma-engraving-preview="overlay"]')
    this.price = this.querySelector('[js-doogma-engraving-preview="price"]')

    this.frontPreview = {
      container: this.querySelector('[js-doogma-engraving-preview="front"]'),
      image: this.querySelector('[js-doogma-engraving-preview="front-image"]'),
      text: this.querySelector('[js-doogma-engraving-preview="front-text"]'),
    }

    this.backPreview = {
      container: this.querySelector('[js-doogma-engraving-preview="back"]'),
      image: this.querySelector('[js-doogma-engraving-preview="back-image"]'),
      text: this.querySelector('[js-doogma-engraving-preview="back-text"]'),
    }

    this.cartDrawerPreviouslyOpen = false
  }

  /**
   * Lifecycle hook - component is attached to the DOM.
   */
  connectedCallback() {
    document.addEventListener('click', (event) => {
      if (!event.target?.matches('[js-doogma-engraving-preview="open"]')) {
        return
      }

      this.open(event.target)
    })

    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close())

    if (this.atcButton) {
      this.atcButton.addEventListener('click', this.addToCart.bind(this))
    }

    if (this.closeButtons?.length) {
      this.closeButtons.forEach((element) => {
        element.addEventListener('click', this.close.bind(this))
      })
    }

    if (this.editButtons?.length) {
      this.editButtons.forEach((element) => {
        element.addEventListener('click', this.edit.bind(this))
      })
    }

    if (this.overlay) {
      this.overlay.addEventListener('click', this.close.bind(this))
    }
  }

  /**
   * Open drawer.
   * - Must be triggered by a button with suitable data attributes.
   * @param {HTMLElement} trigger - Element which triggered drawer.
   */
  open(trigger) {
    const cartDrawer = document.querySelector('cart-drawer')

    if (cartDrawer?.classList.contains('active')) {
      cartDrawer.close()
      this.cartDrawerPreviouslyOpen = true
    }

    this.activeElement = trigger
    setTimeout(this.classList.add('animate', 'active'))

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = document.getElementById('EngravingPreview')
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close')
        trapFocus(containerToTrapFocusOn, focusElement)
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden')
    this.updateTemplate(trigger)
  }

  /**
   * Update template.
   * @param {HTMLElement} trigger - Element which triggered drawer.
   */
  updateTemplate(trigger) {
    if (!trigger) {
      return
    }

    const details = {
      front: {
        image: trigger.dataset.frontPreviewimageurl,
        font: trigger.dataset.frontFont?.replace('pf_', ''),
        text: trigger.dataset.frontText,
      },
      back: {
        image: trigger.dataset.backPreviewimageurl,
        font: trigger.dataset.backFont?.replace('pf_', ''),
        text: trigger.dataset.backText,
      },
    }

    this.price.innerText = trigger.dataset.price
    this.classList.toggle('doogma-engraving-preview--cart', trigger.dataset.location === 'cart')

    Object.keys(details).forEach((location) => {
      if (location === 'back') {
        this.backPreview.container.classList.toggle('is-hidden', trigger.dataset.backengraving === 'false')
      }

      this.updatePreviewTemplate(this[`${location}Preview`], details[location])
    })
  }

  /**
   * Update preview template.
   * @param {Object} elements - Elements to update.
   * @param {Object} data - Engraving data.
   */
  updatePreviewTemplate(elements, data) {
    elements.container?.classList?.toggle('is-active', Boolean(data.text))

    /**
     * Set src to blank to clear the image.
     */
    elements.image.src = ''
    elements.image.src = data.image ? data.image : ''
    elements.text.innerText = data.text ? data.text : ''

    /**
     * Update font classes.
     */
    window.doogmaEngravingFonts.forEach((font) => {
      const className = `doogma-font-${font.split('.')[0]}`
      elements.text.classList.remove(className)
    })

    if (data.font) {
      elements.text.classList.add(`doogma-font-${data.font}`)
    }
  }

  /**
   * Close drawer.
   */
  close() {
    this.classList.remove('active')
    removeTrapFocus(this.activeElement)
    document.body.classList.remove('overflow-hidden')

    /**
     * Re-open cart drawer if previously open.
     */
    if (!this.cartDrawerPreviouslyOpen) {
      return
    }

    this.cartDrawerPreviouslyOpen = false
    const cartDrawer = document.querySelector('cart-drawer')
    cartDrawer.open()
  }

  /**
   * Add to cart.
   */
  addToCart() {
    const productForm = document.querySelector('product-form[js-product-form="form"]')

    if (!productForm) {
      return
    }

    this.close()
    productForm.onSubmitHandler()
  }

  /**
   * Edit engraving.
   */
  edit() {
    const doogmaEngraving = document.querySelector('doogma-engraving')

    if (!doogmaEngraving) {
      return
    }

    this.close()
    doogmaEngraving.open()
  }
}

customElements.define('doogma-engraving-preview', DoogmaEngravingPreview)
